/**
 * Shared reading of one aggregate cell (SYR-111 v3 page).
 *
 * A cell is a mean with a sample behind it, and three things can make it not
 * a number: nobody answered, the models held no belief, or the item did not
 * apply. The page shows those as words, never as a low score.
 */

import { IModelBeliefsCell } from '../../../../services/nestjs-base-api';

export type CellView = 'self' | 'customer';

export const NO_BELIEF_THRESHOLD = 0.5;

export const ANCHORS: Record<number, string> = {
  1: 'Strongly disagree',
  2: 'Disagree',
  3: 'Somewhat disagree',
  4: 'Neutral',
  5: 'Somewhat agree',
  6: 'Agree',
  7: 'Strongly agree',
};

export interface CellReading {
  value: number | null;
  // Why there is no value, when there is none.
  reason: 'no_data' | 'no_belief' | 'not_applicable' | null;
  n: number;
}

export const readCell = (
  cell: IModelBeliefsCell | null | undefined,
  view: CellView,
): CellReading => {
  if (!cell) return { value: null, reason: 'no_data', n: 0 };
  const value = view === 'self' ? cell.self : cell.customer;
  // Each view carries its own rates. A model that holds no belief itself
  // still guesses what customers would say, so the customer view is read
  // against the customer rates, falling back to the shared ones on a read
  // served before they existed.
  const noBeliefRate =
    view === 'customer' ? (cell.customerNoBeliefRate ?? cell.noBeliefRate) : cell.noBeliefRate;
  const naRate = view === 'customer' ? (cell.customerNaRate ?? cell.naRate) : cell.naRate;
  if (value === null || value === undefined) {
    if ((naRate ?? 0) >= NO_BELIEF_THRESHOLD)
      return { value: null, reason: 'not_applicable', n: cell.n };
    if ((noBeliefRate ?? 0) >= NO_BELIEF_THRESHOLD)
      return { value: null, reason: 'no_belief', n: cell.n };
    return { value: null, reason: 'no_data', n: cell.n };
  }
  if ((noBeliefRate ?? 0) >= NO_BELIEF_THRESHOLD)
    return { value: null, reason: 'no_belief', n: cell.n };
  return { value, reason: null, n: cell.n };
};

export const reasonLabel = (reason: CellReading['reason']): string => {
  switch (reason) {
    case 'no_belief':
      return 'No belief held';
    case 'not_applicable':
      return 'Does not apply';
    default:
      return '—';
  }
};

export const anchorFor = (value: number | null, scaleMax: number): string => {
  if (value === null) return '';
  if (scaleMax === 10) return `${value.toFixed(1)} of 10`;
  return ANCHORS[Math.round(value)] ?? '';
};

// Rounded the way the written read rounds (half up on the scaled value), so
// the prose and the table never show two numbers for one score: toFixed(1)
// prints 4.05 as 4.0, the read says 4.1.
export const roundTo = (value: number, dp = 1): number => {
  const scale = 10 ** dp;
  return Math.round(value * scale) / scale;
};

export const fmt = (value: number | null, dp = 1): string =>
  value === null ? '—' : roundTo(value, dp).toFixed(dp);

// A change in the brand's terms: positive is good for the brand, whichever
// way the statement's scale runs (Price Deals and flipped items run the
// other way).
export const deltaInBrandTerms = (
  after: number | null,
  before: number | null,
  higherIsBetter: boolean,
): number | null => {
  if (after === null || before === null) return null;
  const raw = after - before;
  return higherIsBetter ? raw : -raw;
};

// The difference as it reads on the scale, printed beside the score: 5.6 to
// 5.0 is -0.6 whichever way the statement runs. The colour, not the sign,
// says whether it favours the brand (deltaInBrandTerms).
export const rawDelta = (after: number | null, before: number | null): number | null =>
  after === null || before === null ? null : after - before;

export const deltaColour = (delta: number | null): string =>
  delta === null ? '#64748B' : delta < 0 ? '#dc2626' : delta > 0 ? '#059669' : '#64748B';

export const signed = (value: number | null, dp = 1): string =>
  value === null ? '—' : `${value > 0 ? '+' : ''}${roundTo(value, dp).toFixed(dp)}`;

// Group by category, keeping the instrument's order.
export const groupByCategory = <T extends { category: string }>(
  rows: T[],
): { category: string; rows: T[] }[] => {
  const out: { category: string; rows: T[] }[] = [];
  for (const row of rows) {
    const last = out[out.length - 1];
    if (last && last.category === row.category) last.rows.push(row);
    else out.push({ category: row.category, rows: [row] });
  }
  return out;
};

// v4: what the pages say on a statement. Read straight from the cell: its n is
// the pages that speak to the statement and its no-belief rate is the share
// that do not, so the usual no-belief rule would hide a score that a handful
// of pages agree on.
export const readPages = (
  cell: IModelBeliefsCell | null | undefined,
): { value: number | null; pages: number } => ({
  value: cell?.self ?? null,
  pages: cell?.n ?? 0,
});
